import { useCallback, useEffect, useRef } from "react";
import type { RefObject } from "react";

import { FeatureTogglesProvider } from "@/context/feature-toggles";
import { useModal } from "@/context/modal/modal";

import { CrossIcon24, CrossIcon32 } from "@/components/icons";
import { VRIntlProviderComponent } from "@/components/providers/intl-provider";

import {
    CloseContainer,
    ModalBackdrop,
    ModalContent,
    CloseIconContainer,
    Content,
    ExpandableModalTitle,
} from "./modal-with-context.styles";

const localeFn = (target: string) => import(`./locale/${target.toLowerCase()}.json`);

interface ModalProps {
    focusRef?: RefObject<HTMLInputElement>;
}
interface ModalWithContextProps {
    focusRef?: RefObject<HTMLInputElement>;
}

const Modal = ({ focusRef }: ModalProps) => {
    const {
        isModalOpen,
        closeModal,
        modalContent,
        width,
        height,
        minWidth,
        minHeight,
        backgroundColor,
        borderRadius,
        padding,
        boxShadow,
        isExpandableContent,
        expandableContentTitle,
    } = useModal();

    const modalRef = useRef<HTMLDivElement | null>(null);

    const handleClickOutsideModal = useCallback(
        (event: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                closeModal();
            }
        },
        [closeModal, modalRef],
    );

    const handleEscKeyPress = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeModal();
            }
        },
        [closeModal],
    );

    useEffect(() => {
        if (isModalOpen) {
            document.addEventListener("mousedown", handleClickOutsideModal);
            document.addEventListener("keydown", handleEscKeyPress);
            document.body.style.overflow = "hidden"; //to prevent scroll on the background page

            if (focusRef && focusRef.current) {
                focusRef.current.focus();
            }
        } else {
            document.removeEventListener("mousedown", handleClickOutsideModal);
            document.removeEventListener("keydown", handleEscKeyPress);
            document.body.style.overflow = "auto";
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutsideModal);
            document.removeEventListener("keydown", handleEscKeyPress);
            document.body.style.overflow = "auto";
        };
    }, [isModalOpen]);

    if (!isModalOpen) return null;

    return (
        <ModalBackdrop>
            <ModalContent
                data-testid="modal"
                ref={modalRef}
                width={width}
                height={height}
                minWidth={minWidth}
                minHeight={minHeight}
                backgroundColor={backgroundColor}
                borderRadius={borderRadius}
                padding={padding}
                boxShadow={boxShadow}
                isExpandableContent={isExpandableContent}
                expandableContentTitle={expandableContentTitle}
            >
                <CloseContainer
                    data-testid="modal-close-container"
                    isExpandableContent={isExpandableContent}
                >
                    {expandableContentTitle && (
                        <ExpandableModalTitle>{expandableContentTitle}</ExpandableModalTitle>
                    )}

                    <CloseIconContainer
                        onClick={closeModal}
                        isExpandableContent={isExpandableContent}
                    >
                        {isExpandableContent ? <CrossIcon24 /> : <CrossIcon32 />}
                    </CloseIconContainer>
                </CloseContainer>

                <Content data-testid="modal-content">{modalContent}</Content>
            </ModalContent>
        </ModalBackdrop>
    );
};

const ModalWithContext = ({ focusRef }: ModalWithContextProps) => {
    return (
        <FeatureTogglesProvider>
            <VRIntlProviderComponent localeFn={localeFn} id="modal-with-context" fallback={null}>
                <Modal focusRef={focusRef} />
            </VRIntlProviderComponent>
        </FeatureTogglesProvider>
    );
};

export default ModalWithContext;
